/**
 * Add Device Form Schema
 * 
 * Defines the validation for device creation.
 * deviceNumber and other IDs are auto-generated by the backend.
 * 
 * Form metadata (name, title, description) is loaded from form.yml
 */

import { z } from 'zod'
import { FormConfigLoader } from '@/lib/formConfigLoader'
import formYaml from './form.yml?raw'

// Load form configuration from YAML
const formConfig = FormConfigLoader.loadFormConfig(formYaml)

// ============================================================================
// Export Form Metadata from YAML
// ============================================================================

export const FORM_CONFIG = formConfig
export const FORM_NAME = formConfig.formName
export const FORM_TITLE = formConfig.formTitle
export const FORM_DESCRIPTION = formConfig.formDescription

// ============================================================================
// Constants
// ============================================================================

const MIN_NUMBER = 1000000000
const MAX_NUMBER = 9999999999

// ============================================================================
// Enum Types (matching API schema)
// ============================================================================

// All possible device statuses (for reference)
export const AllDeviceStatusEnum = z.enum([
  'ordered',
  'procured',
  'provisioned',
  'retired',
  'faulty',
  'returned',
])

// Statuses available for new device creation
// Other statuses (retired, faulty, returned) require additional info
export const DeviceStatusEnum = z.enum([
  'ordered',
  'procured',
  'retired',
  'faulty',
  'returned',
])
export type DeviceStatus = z.infer<typeof DeviceStatusEnum>

// ============================================================================
// Form Schema (What user fills in)
// ============================================================================

export const AddDeviceFormSchema = z.object({
  // Optional to match the backend: upsertDevice requires only device.deviceStatus.
  // deviceName is optional there (maxLength 255 if provided).
  deviceName: z
    .string()
    .max(255, 'Name cannot exceed 255 characters')
    .optional()
    .or(z.literal('')),
  
  // UI-only: used to filter device types (not sent to API)
  manufacturerNumber: z
    .coerce.number()
    .gte(MIN_NUMBER)
    .lte(MAX_NUMBER)
    .optional(),

  // Optional to match the backend (deviceTypeNumber is nullable; only deviceStatus
  // is required). Empty selection ('') is treated as "not set".
  deviceTypeNumber: z.preprocess(
    (v) => (v === '' || v == null ? undefined : v),
    z.coerce.number()
      .gte(MIN_NUMBER, 'Invalid device type')
      .lte(MAX_NUMBER, 'Invalid device type')
      .optional(),
  ),

  // Optional: links device to a device profile (drives network type; Feature 6).
  // Sent on the device; backend persistence pending (device→profile link hand-off).
  deviceProfileNumber: z
    .coerce.number()
    .gte(MIN_NUMBER)
    .lte(MAX_NUMBER)
    .optional(),

  // Optional: where the device was purchased
  distributorNumber: z
    .coerce.number()
    .gte(MIN_NUMBER)
    .lte(MAX_NUMBER)
    .optional(),

  // UI-only: whether ordering from manufacturer or distributor
  orderedFrom: z.enum(['manufacturer', 'distributor']).optional(),

  // Required: current status of the device
  deviceStatus: DeviceStatusEnum,

  // Purchase Order Number — optional, 10-digit system-generated number
  devicePurchaseOrderNumber: z.coerce.number()
    .gte(1000000000, 'Must be a valid 10-digit PO number')
    .lte(9999999999, 'Must be a valid 10-digit PO number')
    .optional(),

  // Order Date — optional
  deviceOrderDate: z.string().optional(),
})

export type AddDeviceFormData = z.infer<typeof AddDeviceFormSchema>

/**
 * Transform form data to API request format.
 * Converts the flat form structure to the nested API structure.
 * deviceNumber is auto-generated by the backend.
 */
export function transformToApiRequest(formData: AddDeviceFormData) {
  return {
    device: {
      // Omit when empty — backend deviceName is optional but has minLength 1 if sent.
      deviceName: formData.deviceName?.trim() || undefined,
      deviceStatus: formData.deviceStatus,
      deviceTypeNumber: formData.deviceTypeNumber,
      deviceProfileNumber: formData.deviceProfileNumber,
    },
    procurementInfo: {
      deviceDistributorNumber: formData.distributorNumber,
      deviceTypeNumber: formData.deviceTypeNumber,
      devicePurchaseOrderNumber: formData.devicePurchaseOrderNumber,
      deviceOrderDate: formData.deviceOrderDate ? new Date(formData.deviceOrderDate).getTime() / 1000 : undefined,
    },
  }
}
